import type { CSSProperties, ReactNode } from "react";
import Button from "@material-ui/core/Button";
import type { ButtonProps } from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Paper from "@material-ui/core/Paper";
import VideocamIcon from "@material-ui/icons/Videocam";
import StopIcon from "@material-ui/icons/Stop";
import ReplayIcon from "@material-ui/icons/Replay";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import AttestationGuideImage from "./attestation-guide.png";
import type { CameraStatus } from "./useMediaRecorder";

const RAMPVER_GREEN = "#256141";
const DANGER_RED = "#ef4444";
const INSTRUCTION_VIDEO_EMBED_URL = "https://www.youtube.com/embed/kSqQXsVcGU0";
const INSTRUCTION_VIDEO_URL = "https://www.youtube.com/shorts/kSqQXsVcGU0";
const PREPARATION_TIPS = [
  "Hold the same valid ID you submitted during identity verification.",
  "Keep your face and the ID clearly visible, in good light.",
  "Record in one continuous take of under one minute, with your voice clear.",
  "Allow camera and microphone access when your browser asks.",
];
const ATTESTATION_STEPS = [
  { key: "prepare", label: "Prepare" },
  { key: "record", label: "Record" },
  { key: "review", label: "Review" },
] as const;
const FILL_FRAME_STYLE: CSSProperties = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
};
const BUTTON_TONE_STYLES = {
  primary: { backgroundColor: RAMPVER_GREEN, color: "#ffffff" },
  secondary: { backgroundColor: "#ffffff", color: RAMPVER_GREEN, borderColor: RAMPVER_GREEN },
  danger: { backgroundColor: DANGER_RED, color: "#ffffff" },
};

export type AttestationStepKey = typeof ATTESTATION_STEPS[number]["key"];
export type ReviewVariant = "existing" | "preview" | "uploading" | "success" | "error";

export interface AttestationScriptValues {
  fullName: string;
  birthDate: string;
  today: string;
}

export const AttestationCard = ({
  activeStep,
  children,
}: {
  activeStep?: AttestationStepKey;
  children: ReactNode;
}) => (
  <Paper elevation={2} className="font-poppins p-4 md:p-6">
    <h1 className="text-xl md:text-2xl font-bold mt-0 mb-2">Video Attestation</h1>
    {activeStep && <AttestationProgress activeStep={activeStep} />}
    {children}
  </Paper>
);

export const PrepareStep = ({
  isMobile,
  script,
  disabled,
  onEnableCamera,
  onPreviewId,
}: {
  isMobile: boolean;
  script: AttestationScriptValues;
  disabled: boolean;
  onEnableCamera: () => void;
  onPreviewId: () => void;
}) => (
  <div className="grid gap-6 md:grid-cols-2">
    <div className="grid gap-4 content-start">
      <p className="text-gray-700 m-0">
        Record a short video of yourself holding your valid ID while reading the script below.
      </p>
      <ul className="list-disc pl-5 m-0 text-gray-700">
        {PREPARATION_TIPS.map((tip) => (
          <li key={tip} className="mb-1">{tip}</li>
        ))}
      </ul>
      <img
        src={AttestationGuideImage}
        alt="Person holding an ID next to their face, with both clearly visible"
        className="w-full rounded-lg"
      />
      <div className="rounded-lg p-4" style={{ backgroundColor: "#fff8e1", borderLeft: "6px solid #d4a72c" }}>
        <p className="text-xs font-bold uppercase tracking-wide m-0 mb-2" style={{ color: RAMPVER_GREEN }}>
          What you&apos;ll say
        </p>
        <p className="m-0" style={{ lineHeight: 1.8 }}>
          <ScriptText script={script} />
        </p>
      </div>
    </div>
    <div className="grid gap-2 content-start">
      <p className="font-semibold m-0">Watch the sample first</p>
      <div className="relative w-full mx-auto overflow-hidden rounded-lg" style={{ maxWidth: isMobile ? 320 : 280, backgroundColor: "#000" }}>
        <div style={{ paddingTop: "177.78%" }} />
        <iframe
          title="Video attestation instructions"
          src={INSTRUCTION_VIDEO_EMBED_URL}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          style={{ ...FILL_FRAME_STYLE, border: 0 }}
        />
      </div>
      <a
        href={INSTRUCTION_VIDEO_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-2 text-sm font-semibold no-underline"
        style={{ color: RAMPVER_GREEN }}
      >
        <OpenInNewIcon style={{ fontSize: 18 }} />
        Watch on YouTube
      </a>
    </div>
    <div className="md:col-span-2">
      <ActionRow>
        <ActionButton tone="secondary" onClick={onPreviewId}>Preview ID</ActionButton>
        <ActionButton startIcon={<VideocamIcon />} onClick={onEnableCamera} disabled={disabled}>
          Enable Camera
        </ActionButton>
      </ActionRow>
    </div>
  </div>
);

export const RecordStep = ({
  isMobile,
  script,
  cameraStatus,
  isRecording,
  secondsLeft,
  attachLiveVideo,
  disabled,
  onStartRecording,
  onStopRecording,
  onRetryCamera,
  onPreviewId,
}: {
  isMobile: boolean;
  script: AttestationScriptValues;
  cameraStatus: CameraStatus;
  isRecording: boolean;
  secondsLeft: number;
  attachLiveVideo: (element: HTMLVideoElement | null) => void;
  disabled: boolean;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onRetryCamera: () => void;
  onPreviewId: () => void;
}) => {
  if (cameraStatus === "error") {
    return (
      <div className="grid gap-4 justify-items-center text-center py-8">
        <ErrorOutlineIcon style={{ fontSize: 48, color: DANGER_RED }} />
        <p className="m-0" style={{ color: DANGER_RED }}>
          Unable to access your camera and microphone. Please allow browser access to your camera and
          microphone, then try again.
        </p>
        <ActionButton onClick={onRetryCamera} disabled={disabled}>Retry Camera Access</ActionButton>
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      <MediaFrame isMobile={isMobile}>
        <video
          ref={attachLiveVideo}
          autoPlay
          muted
          playsInline
          aria-label="Live camera preview"
          style={{ ...FILL_FRAME_STYLE, objectFit: "cover", transform: "scaleX(-1)" }}
        />
        <div className="absolute inset-x-0 top-0 p-3">
          <p
            className="m-0 rounded-lg p-3 text-sm"
            style={{ backgroundColor: "rgba(255,255,255,0.92)", color: "#111827", lineHeight: 1.6 }}
          >
            <ScriptText script={script} />
          </p>
        </div>
        {cameraStatus === "starting" && (
          <p className="absolute inset-x-0 bottom-0 m-0 p-4 text-center text-sm text-white" role="status">
            Waiting for camera and microphone access...
          </p>
        )}
        {isRecording && <RecordingBadge secondsLeft={secondsLeft} />}
      </MediaFrame>
      <p className="text-center text-sm text-gray-600 m-0">
        Hold your ID next to your face and read the script aloud in one take.
      </p>
      <ActionRow>
        {!isRecording && <ActionButton tone="secondary" onClick={onPreviewId}>Preview ID</ActionButton>}
        {isRecording ? (
          <ActionButton tone="danger" startIcon={<StopIcon />} onClick={onStopRecording}>
            Stop Recording
          </ActionButton>
        ) : (
          <ActionButton
            startIcon={<VideocamIcon />}
            onClick={onStartRecording}
            disabled={disabled || cameraStatus !== "live"}
          >
            Start Recording
          </ActionButton>
        )}
      </ActionRow>
    </div>
  );
};

export const ReviewStep = ({
  isMobile,
  variant,
  videoUrl,
  isEmbedded,
  errorMessage,
  disabled,
  canRetryUpload,
  onReRecord,
  onSubmit,
}: {
  isMobile: boolean;
  variant: ReviewVariant;
  videoUrl: string | null;
  isEmbedded: boolean;
  errorMessage: string;
  disabled: boolean;
  canRetryUpload: boolean;
  onReRecord: () => void;
  onSubmit: () => void;
}) => (
  <div className="grid gap-4">
    <p className="text-lg font-semibold text-center m-0">
      {variant === "existing" ? "Your submitted video" : "Review your recording"}
    </p>
    <MediaFrame isMobile={isMobile}>
      {videoUrl && (
        <video
          key={videoUrl}
          src={videoUrl}
          controls
          playsInline
          preload="metadata"
          style={{ ...FILL_FRAME_STYLE, objectFit: "contain" }}
        />
      )}
    </MediaFrame>

    {variant === "existing" && (
      <>
        <ReviewHint>This video is on file. Re-record only if you need to replace it.</ReviewHint>
        <ActionRow>
          <ActionButton startIcon={<ReplayIcon />} onClick={onReRecord} disabled={disabled}>Re-record</ActionButton>
        </ActionRow>
      </>
    )}

    {variant === "preview" && (
      <>
        <ReviewHint>Check that your face, ID, and voice are clear before submitting.</ReviewHint>
        <ActionRow>
          <ActionButton tone="secondary" startIcon={<ReplayIcon />} onClick={onReRecord} disabled={disabled}>
            Re-record
          </ActionButton>
          <ActionButton startIcon={<CloudUploadIcon />} onClick={onSubmit} disabled={disabled}>
            Submit Recording
          </ActionButton>
        </ActionRow>
      </>
    )}

    {variant === "uploading" && (
      <ActionRow>
        <ActionButton disabled>
          <CircularProgress size={20} style={{ marginRight: 8, color: "#ffffff" }} />
          Submitting...
        </ActionButton>
      </ActionRow>
    )}

    {variant === "success" && (
      <>
        <StatusLine tone="success">
          {isEmbedded ? "Recording submitted successfully!" : "Success! Your video attestation has been submitted."}
        </StatusLine>
        {isEmbedded ? (
          <ActionRow>
            <ActionButton tone="secondary" startIcon={<ReplayIcon />} onClick={onReRecord} disabled={disabled}>
              Re-record & Resubmit
            </ActionButton>
          </ActionRow>
        ) : (
          <ReviewHint>Redirecting you back to your dashboard...</ReviewHint>
        )}
      </>
    )}

    {variant === "error" && (
      <>
        <StatusLine tone="error">{errorMessage}</StatusLine>
        <ActionRow>
          <ActionButton tone="secondary" startIcon={<ReplayIcon />} onClick={onReRecord} disabled={disabled}>
            Re-record
          </ActionButton>
          <ActionButton onClick={onSubmit} disabled={disabled || !canRetryUpload}>Try Again</ActionButton>
        </ActionRow>
      </>
    )}
  </div>
);

export const IdPreviewDialog = ({
  isOpen,
  url,
  loading,
  error,
  close,
}: {
  isOpen: boolean;
  url: string | null;
  loading: boolean;
  error: string;
  close: () => void;
}) => (
  <Dialog open={isOpen} onClose={close} fullWidth maxWidth="sm">
    <DialogTitle className="font-poppins">Show this ID during recording</DialogTitle>
    <DialogContent dividers>
      {loading ? (
        <div className="flex items-center justify-center" style={{ minHeight: 260 }}>
          <CircularProgress style={{ color: RAMPVER_GREEN }} />
        </div>
      ) : error ? (
        <div className="font-poppins flex items-center justify-center text-center text-gray-600" style={{ minHeight: 180 }}>
          {error}
        </div>
      ) : url ? (
        <img src={url} alt="Submitted front side of ID" className="block w-full rounded-lg" />
      ) : null}
    </DialogContent>
    <DialogActions>
      <Button onClick={close} style={{ color: RAMPVER_GREEN }}>Close</Button>
    </DialogActions>
  </Dialog>
);

const AttestationProgress = ({ activeStep }: { activeStep: AttestationStepKey }) => {
  const activeIndex = ATTESTATION_STEPS.findIndex((step) => step.key === activeStep);

  return (
    <ol className="flex flex-wrap gap-x-5 gap-y-1 list-none p-0 mt-0 mb-5 text-sm" aria-label="Video attestation progress">
      {ATTESTATION_STEPS.map((step, index) => {
        const isReached = index <= activeIndex;
        return (
          <li
            key={step.key}
            aria-current={index === activeIndex ? "step" : undefined}
            className="flex items-center gap-2"
            style={{ color: isReached ? RAMPVER_GREEN : "#9ca3af", fontWeight: index === activeIndex ? 700 : 500 }}
          >
            <span
              className="inline-flex items-center justify-center rounded-full text-xs"
              style={{
                width: 22,
                height: 22,
                border: "1.5px solid currentColor",
                ...(index < activeIndex ? { backgroundColor: RAMPVER_GREEN, color: "#ffffff" } : {}),
              }}
            >
              {index + 1}
            </span>
            {step.label}
          </li>
        );
      })}
    </ol>
  );
};

const ScriptText = ({ script }: { script: AttestationScriptValues }) => (
  <>
    {"I am "}
    <ScriptValue value={script.fullName} placeholder="[Full Name]" />
    {", born on "}
    <ScriptValue value={script.birthDate} placeholder="[Birthday]" />
    {". I am confirming my request to verify my Rampver Online account today, "}
    <ScriptValue value={script.today} placeholder="[Date Today]" />
    {"."}
  </>
);

const ScriptValue = ({ value, placeholder }: { value: string; placeholder: string }) => (
  <span style={value ? { color: RAMPVER_GREEN, fontWeight: 700 } : { color: "#6b7280", fontWeight: 600 }}>
    {value || placeholder}
  </span>
);

const MediaFrame = ({ isMobile, children }: { isMobile: boolean; children: ReactNode }) => (
  <div
    className="relative w-full mx-auto overflow-hidden rounded-lg"
    style={{ maxWidth: isMobile ? 320 : 640, backgroundColor: "#000" }}
  >
    {/* 3:4 on phones keeps the record/stop controls above the fold under the app header. */}
    <div style={{ paddingTop: isMobile ? "133.33%" : "56.25%" }} />
    {children}
  </div>
);

const RecordingBadge = ({ secondsLeft }: { secondsLeft: number }) => (
  <div
    className="absolute bottom-0 left-0 m-3 flex items-center gap-2 rounded-full px-3 py-1 text-sm text-white"
    style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
  >
    <span className="rounded-full animate-pulse" style={{ width: 10, height: 10, backgroundColor: DANGER_RED }} />
    REC
    <span aria-live="polite">{formatSecondsLeft(secondsLeft)} left</span>
  </div>
);

const ReviewHint = ({ children }: { children: ReactNode }) => (
  <p className="text-center text-sm text-gray-600 m-0">{children}</p>
);

const StatusLine = ({ tone, children }: { tone: "success" | "error"; children: ReactNode }) => (
  <div
    className="flex items-center justify-center gap-2 text-center"
    style={{ color: tone === "success" ? "#16a34a" : DANGER_RED }}
  >
    {tone === "success" ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
    <span className="font-medium">{children}</span>
  </div>
);

const ActionRow = ({ children }: { children: ReactNode }) => (
  <div className="grid gap-3 md:flex md:justify-center">{children}</div>
);

const ActionButton = ({
  tone = "primary",
  disabled,
  ...buttonProps
}: Omit<ButtonProps, "style" | "variant"> & { tone?: keyof typeof BUTTON_TONE_STYLES }) => (
  <Button
    variant={tone === "secondary" ? "outlined" : "contained"}
    size="large"
    disabled={disabled}
    style={{
      ...BUTTON_TONE_STYLES[tone],
      textTransform: "none",
      fontWeight: 600,
      borderRadius: 8,
      ...(disabled ? { opacity: 0.6 } : {}),
    }}
    {...buttonProps}
  />
);

const formatSecondsLeft = (seconds: number) => {
  const safeSeconds = Math.max(0, seconds);
  return `${Math.floor(safeSeconds / 60)}:${(safeSeconds % 60).toString().padStart(2, "0")}`;
};
